import React, { useState, useEffect, useCallback } from "react";
import {
	Box,
	Grid,
	Typography,
	Chip,
	Switch,
	Button,
	IconButton,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Tabs,
	Tab,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	CircularProgress,
	Alert,
	Tooltip,
	Divider,
	Stack,
} from "@mui/material";
import {
	PlayArrow as PlayArrowIcon,
	Send as SendIcon,
	Visibility as VisibilityIcon,
	EditCalendar as EditCalendarIcon,
	History as HistoryIcon,
	Refresh as RefreshIcon,
	ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import api from "../../../services/Axios";
import { Helmet } from "react-helmet";

// ───────────────────────── Shared dark-dashboard styles ─────────────────────────
// Matches Dashboard.jsx exactly so this page doesn't look like a bolted-on
// MUI-default page next to the rest of the admin.
const CHART_STYLE = { backgroundColor: "#161717" };
const CARD_TITLE = { color: "#ccc", fontWeight: 700, mb: 0.5 };
const CARD_SUB = { color: "#555", fontSize: 12, mb: 2 };
const TABLE_HEAD_CELL = { color: "#888", borderColor: "#2a2a2a", fontSize: 12, whiteSpace: "nowrap" };
const TABLE_BODY_CELL = { color: "#ddd", borderColor: "#2a2a2a", fontSize: 13, whiteSpace: "nowrap" };
const ACCENT = "#4ade80"; // active/success — matches Dashboard.jsx's green
const WARN = "#fbbf24";
const DANGER = "#fb7185";
const INFO = "#60a5fa";

const CRON_PRESETS = [
	{ label: "Daily @ 10:00 UTC", value: "0 10 * * *" },
	{ label: "Weekdays @ 10:00 UTC", value: "0 10 * * 1-5" },
	{ label: "Weekly — Mon @ 10:00 UTC", value: "0 10 * * 1" },
	{ label: "Custom…", value: "custom" },
];

function timeAgo(iso) {
	if (!iso) return "—";
	const diffMs = Date.now() - new Date(iso).getTime();
	const mins = Math.floor(diffMs / 60000);
	if (mins < 1) return "just now";
	if (mins < 60) return `${mins}m ago`;
	const hrs = Math.floor(mins / 60);
	if (hrs < 24) return `${hrs}h ago`;
	const days = Math.floor(hrs / 24);
	return `${days}d ago`;
}

function statusDotColor(status) {
	switch (status) {
		case "completed":
		case "sent":
			return ACCENT;
		case "failed":
			return DANGER;
		case "running":
			return WARN;
		case "skipped":
			return "#888";
		default:
			return "#888";
	}
}

function StatusChip({ label }) {
	const color = statusDotColor(label);
	return (
		<Typography
			component="span"
			fontSize={11}
			sx={{
				display: "inline-block",
				px: 0.75,
				py: 0.25,
				borderRadius: 0.75,
				backgroundColor: `${color}18`,
				color,
				whiteSpace: "nowrap",
			}}
		>
			{label}
		</Typography>
	);
}

// ---------------------------------------------------------------------------
// Preview modal — GET rendered HTML/text without sending anything
// ---------------------------------------------------------------------------
function PreviewDialog({ open, onClose, campaign, creatorId }) {
	const [firstName, setFirstName] = useState("Test");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [preview, setPreview] = useState(null);

	const runPreview = useCallback(async () => {
		if (!campaign) return;
		setLoading(true);
		setError("");
		try {
			const res = await api.post(`/admin/email/campaigns/${campaign.id}/preview`, {
				first_name: firstName,
				creator_id: creatorId,
			});
			setPreview(res?.data);
		} catch (err) {
			setError(err?.response?.data?.detail || err.message);
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campaign, creatorId, firstName]);

	useEffect(() => {
		if (open) runPreview();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { backgroundColor: "#161717" } }}>
			<DialogTitle sx={{ color: "#ccc" }}>Preview — {campaign?.name}</DialogTitle>
			<DialogContent>
				<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
					<TextField
						label="Sample first name"
						size="small"
						value={firstName}
						onChange={(e) => setFirstName(e.target.value)}
						sx={{
							"& .MuiOutlinedInput-root": { color: "#ddd", backgroundColor: "#1e1e1e" },
							"& .MuiInputLabel-root": { color: "#888" },
							"& .MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
						}}
					/>
					<Button variant="outlined" onClick={runPreview} disabled={loading} sx={{ color: "#ccc", borderColor: "#333" }}>
						Re-render
					</Button>
				</Stack>

				{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

				{loading && (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress size={28} />
					</Box>
				)}

				{!loading && preview && (
					<>
						<Typography sx={CARD_TITLE}>Subject</Typography>
						<Typography sx={{ color: "#ddd", mb: 2 }}>{preview.subject}</Typography>

						<Typography sx={CARD_TITLE}>HTML body</Typography>
						<Box sx={{ border: "1px solid #2a2a2a", borderRadius: 1, overflow: "hidden", mb: 2 }}>
							<iframe
								title="email-preview"
								srcDoc={preview.html}
								sandbox=""
								style={{ width: "100%", height: 420, border: "none", background: "#fff" }}
							/>
						</Box>

						<Typography sx={CARD_TITLE}>Plain text fallback</Typography>
						<Box
							sx={{
								p: 2,
								whiteSpace: "pre-wrap",
								fontFamily: "monospace",
								fontSize: 13,
								color: "#ccc",
								border: "1px solid #2a2a2a",
								borderRadius: 1,
								backgroundColor: "#1e1e1e",
							}}
						>
							{preview.text}
						</Box>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} sx={{ color: "#888" }}>Close</Button>
			</DialogActions>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Schedule editor — activate/deactivate + cron + cooldown + audience filter
// ---------------------------------------------------------------------------
function ScheduleDialog({ open, onClose, campaign, creatorId, onSaved }) {
	const [isActive, setIsActive] = useState(false);
	const [cronPreset, setCronPreset] = useState("0 10 * * *");
	const [customCron, setCustomCron] = useState("");
	const [cooldownDays, setCooldownDays] = useState(7);
	const [appUrl, setAppUrl] = useState("");
	const [externalOnly, setExternalOnly] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		if (!campaign) return;
		setIsActive(!!campaign.is_active);
		setCooldownDays(campaign.cooldown_days ?? 7);
		setAppUrl(campaign.app_url || "");
		setExternalOnly(campaign.audience_filter?.external_only ?? true);
		const known = CRON_PRESETS.find((p) => p.value === campaign.cron_expression);
		if (known) {
			setCronPreset(known.value);
		} else if (campaign.cron_expression) {
			setCronPreset("custom");
			setCustomCron(campaign.cron_expression);
		}
	}, [campaign]);

	const handleSave = async () => {
		if (!campaign) return;
		setSaving(true);
		setError("");
		try {
			const cron_expression = cronPreset === "custom" ? customCron : cronPreset;
			await api.patch(`/admin/email/campaigns/${campaign.id}`, {
				creator_id: creatorId,
				is_active: isActive,
				cron_expression,
				cooldown_days: Number(cooldownDays),
				app_url: appUrl,
				audience_filter: { external_only: externalOnly },
			});
			onSaved();
			onClose();
		} catch (err) {
			setError(err?.response?.data?.detail || err.message);
		} finally {
			setSaving(false);
		}
	};

	const selectSx = {
		color: "#ddd",
		backgroundColor: "#1e1e1e",
		"& .MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: "#161717" } }}>
			<DialogTitle sx={{ color: "#ccc" }}>Schedule — {campaign?.name}</DialogTitle>
			<DialogContent>
				{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

				<Stack spacing={2} sx={{ mt: 1 }}>
					<Stack direction="row" alignItems="center" justifyContent="space-between">
						<Typography sx={{ color: "#ccc" }}>Active</Typography>
						<Switch
							checked={isActive}
							onChange={(e) => setIsActive(e.target.checked)}
							sx={{ "& .Mui-checked": { color: ACCENT }, "& .Mui-checked + .MuiSwitch-track": { backgroundColor: ACCENT } }}
						/>
					</Stack>

					<FormControl fullWidth size="small">
						<InputLabel sx={{ color: "#888" }}>Schedule</InputLabel>
						<Select label="Schedule" value={cronPreset} onChange={(e) => setCronPreset(e.target.value)} sx={selectSx}>
							{CRON_PRESETS.map((p) => (
								<MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
							))}
						</Select>
					</FormControl>

					{cronPreset === "custom" && (
						<TextField
							label="Cron expression (UTC)"
							size="small"
							value={customCron}
							onChange={(e) => setCustomCron(e.target.value)}
							placeholder="0 10 * * 1"
							helperText="Standard 5-field cron, evaluated in UTC"
							sx={{ "& .MuiOutlinedInput-root": { color: "#ddd", backgroundColor: "#1e1e1e" }, "& .MuiInputLabel-root": { color: "#888" } }}
						/>
					)}

					<TextField
						label="Cooldown (days)"
						size="small"
						type="number"
						value={cooldownDays}
						onChange={(e) => setCooldownDays(e.target.value)}
						helperText="Skip a user if they already got this campaign within N days"
						sx={{ "& .MuiOutlinedInput-root": { color: "#ddd", backgroundColor: "#1e1e1e" }, "& .MuiInputLabel-root": { color: "#888" } }}
					/>

					<TextField
						label="App URL"
						size="small"
						value={appUrl}
						onChange={(e) => setAppUrl(e.target.value)}
						placeholder="https://app.tlknow.ai/reya"
						sx={{ "& .MuiOutlinedInput-root": { color: "#ddd", backgroundColor: "#1e1e1e" }, "& .MuiInputLabel-root": { color: "#888" } }}
					/>

					<Stack direction="row" alignItems="center" justifyContent="space-between">
						<Box>
							<Typography sx={{ color: "#ccc" }}>External users only</Typography>
							<Typography variant="caption" sx={{ color: "#666" }}>
								Excludes load-test, team, and internal accounts
							</Typography>
						</Box>
						<Switch
							checked={externalOnly}
							onChange={(e) => setExternalOnly(e.target.checked)}
							sx={{ "& .Mui-checked": { color: ACCENT }, "& .Mui-checked + .MuiSwitch-track": { backgroundColor: ACCENT } }}
						/>
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} sx={{ color: "#888" }}>Cancel</Button>
				<Button variant="contained" onClick={handleSave} disabled={saving} sx={{ bgcolor: ACCENT, color: "#0b1a10", "&:hover": { bgcolor: "#3fc472" } }}>
					{saving ? "Saving…" : "Save schedule"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Dry-run / Send now modal
// ---------------------------------------------------------------------------
function RunDialog({ open, onClose, campaign, creatorId, mode, onCompleted }) {
	// mode: 'dry-run' | 'send'
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [result, setResult] = useState(null);
	const [confirmText, setConfirmText] = useState("");

	const runDryRun = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const res = await api.post(`/admin/email/campaigns/${campaign.id}/dry-run`, {
				creator_id: creatorId,
			});
			setResult(res?.data);
		} catch (err) {
			setError(err?.response?.data?.detail || err.message);
		} finally {
			setLoading(false);
		}
	}, [campaign, creatorId]);

	useEffect(() => {
		if (open) {
			setResult(null);
			setError("");
			setConfirmText("");
			if (mode === "dry-run") runDryRun();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, mode]);

	const runSend = async () => {
		setLoading(true);
		setError("");
		try {
			const res = await api.post(`/admin/email/campaigns/${campaign.id}/send`, {
				creator_id: creatorId,
			});
			setResult(res?.data);
			onCompleted?.();
		} catch (err) {
			setError(err?.response?.data?.detail || err.message);
		} finally {
			setLoading(false);
		}
	};

	const canConfirmSend = confirmText.trim().toUpperCase() === "SEND";

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: "#161717" } }}>
			<DialogTitle sx={{ color: "#ccc" }}>
				{mode === "send" ? `Send now — ${campaign?.name}` : `Dry run — ${campaign?.name}`}
			</DialogTitle>
			<DialogContent>
				{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

				{mode === "send" && !result && (
					<>
						<Alert severity="warning" sx={{ mb: 2 }}>
							This sends real email via SES. Run a dry run first if you haven't already.
						</Alert>
						<Typography sx={{ mb: 2, color: "#ccc" }}>
							You are about to send <strong>{campaign?.name}</strong> to the current audience
							for <strong>{creatorId}</strong>. Type <strong>SEND</strong> to confirm.
						</Typography>
						<TextField
							fullWidth
							size="small"
							value={confirmText}
							onChange={(e) => setConfirmText(e.target.value)}
							placeholder="SEND"
							sx={{ "& .MuiOutlinedInput-root": { color: "#ddd", backgroundColor: "#1e1e1e" } }}
						/>
					</>
				)}

				{loading && (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress size={28} />
					</Box>
				)}

				{!loading && result && (
					<Stack spacing={1}>
						<Stack direction="row" spacing={2}>
							<StatusChip label={`Sent: ${result.sent_count ?? 0}`} />
							<StatusChip label={`Skipped: ${result.skipped_count ?? 0}`} />
							<StatusChip label={`Failed: ${result.failed_count ?? 0}`} />
						</Stack>
						{Array.isArray(result.recipients) && result.recipients.length > 0 && (
							<Box sx={{ mt: 1, maxHeight: 300, overflowY: "auto", border: "1px solid #2a2a2a", borderRadius: 1 }}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell sx={TABLE_HEAD_CELL}>Email</TableCell>
											<TableCell sx={TABLE_HEAD_CELL}>Status</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{result.recipients.map((r, i) => (
											<TableRow key={i}>
												<TableCell sx={TABLE_BODY_CELL}>{r.email}</TableCell>
												<TableCell sx={TABLE_BODY_CELL}><StatusChip label={r.status} /></TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						)}
					</Stack>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} sx={{ color: "#888" }}>Close</Button>
				{mode === "send" && !result && (
					<Button variant="contained" color="error" disabled={!canConfirmSend || loading} onClick={runSend}>
						Confirm send
					</Button>
				)}
				{mode === "dry-run" && !result && !loading && (
					<Button variant="contained" onClick={runDryRun} sx={{ bgcolor: ACCENT, color: "#0b1a10", "&:hover": { bgcolor: "#3fc472" } }}>
						Run again
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// History tab
// ---------------------------------------------------------------------------
function HistoryTab({ creatorId }) {
	const [runs, setRuns] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [expandedRun, setExpandedRun] = useState(null);
	const [recipients, setRecipients] = useState([]);

	const loadRuns = useCallback(async () => {
		if (!creatorId) return;
		setLoading(true);
		setError("");
		try {
			const res = await api.get(`/admin/email/runs?creator_id=${encodeURIComponent(creatorId)}&limit=50`);
			setRuns(res?.data?.runs || []);
		} catch (err) {
			setError(err?.response?.data?.detail || err.message);
		} finally {
			setLoading(false);
		}
	}, [creatorId]);

	useEffect(() => {
		loadRuns();
	}, [loadRuns]);

	const toggleExpand = async (run) => {
		if (expandedRun === run.id) {
			setExpandedRun(null);
			return;
		}
		setExpandedRun(run.id);
		try {
			const res = await api.get(`/admin/email/runs/${run.id}/recipients`);
			setRecipients(res?.data?.recipients || []);
		} catch (err) {
			setError(err?.response?.data?.detail || err.message);
		}
	};

	return (
		<Box borderRadius={2} p={2.5} sx={CHART_STYLE}>
			<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
				<Box>
					<Typography sx={{ ...CARD_TITLE, mb: 0 }}>Send history</Typography>
					<Typography sx={CARD_SUB}>{runs.length} runs</Typography>
				</Box>
				<IconButton onClick={loadRuns} size="small" sx={{ color: "#888" }}>
					<RefreshIcon fontSize="small" />
				</IconButton>
			</Stack>

			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
					<CircularProgress size={28} />
				</Box>
			) : (
				<Box sx={{ overflowX: "auto" }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={TABLE_HEAD_CELL}>Run</TableCell>
								<TableCell sx={TABLE_HEAD_CELL}>Campaign</TableCell>
								<TableCell sx={TABLE_HEAD_CELL}>Started</TableCell>
								<TableCell sx={TABLE_HEAD_CELL}>Triggered by</TableCell>
								<TableCell sx={TABLE_HEAD_CELL}>Mode</TableCell>
								<TableCell sx={{ ...TABLE_HEAD_CELL, textAlign: "right" }}>Sent</TableCell>
								<TableCell sx={{ ...TABLE_HEAD_CELL, textAlign: "right" }}>Skipped</TableCell>
								<TableCell sx={{ ...TABLE_HEAD_CELL, textAlign: "right" }}>Failed</TableCell>
								<TableCell sx={TABLE_HEAD_CELL}>Status</TableCell>
								<TableCell sx={TABLE_HEAD_CELL} />
							</TableRow>
						</TableHead>
						<TableBody>
							{runs.length === 0 && (
								<TableRow>
									<TableCell colSpan={10} sx={{ ...TABLE_BODY_CELL, textAlign: "center", color: "#555", py: 3 }}>
										No runs yet for this creator.
									</TableCell>
								</TableRow>
							)}
							{runs.map((run) => (
								<React.Fragment key={run.id}>
									<TableRow hover onClick={() => toggleExpand(run)} sx={{ cursor: "pointer" }}>
										<TableCell sx={TABLE_BODY_CELL}>#{run.id}</TableCell>
										<TableCell sx={TABLE_BODY_CELL}>{run.campaign_id}</TableCell>
										<TableCell sx={TABLE_BODY_CELL}>{timeAgo(run.started_at)}</TableCell>
										<TableCell sx={TABLE_BODY_CELL}>{run.triggered_by}</TableCell>
										<TableCell sx={TABLE_BODY_CELL}>{run.mode}</TableCell>
										<TableCell sx={{ ...TABLE_BODY_CELL, textAlign: "right" }}>{run.sent_count}</TableCell>
										<TableCell sx={{ ...TABLE_BODY_CELL, textAlign: "right" }}>{run.skipped_count}</TableCell>
										<TableCell sx={{ ...TABLE_BODY_CELL, textAlign: "right" }}>{run.failed_count}</TableCell>
										<TableCell sx={TABLE_BODY_CELL}><StatusChip label={run.status} /></TableCell>
										<TableCell sx={TABLE_BODY_CELL}>
											<ExpandMoreIcon
												fontSize="small"
												sx={{
													color: "#666",
													transform: expandedRun === run.id ? "rotate(180deg)" : "none",
													transition: "transform 0.15s",
												}}
											/>
										</TableCell>
									</TableRow>
									{expandedRun === run.id && (
										<TableRow>
											<TableCell colSpan={10} sx={{ backgroundColor: "#1a1a1a", borderColor: "#2a2a2a" }}>
												<Table size="small">
													<TableHead>
														<TableRow>
															<TableCell sx={TABLE_HEAD_CELL}>Email</TableCell>
															<TableCell sx={TABLE_HEAD_CELL}>User ID</TableCell>
															<TableCell sx={TABLE_HEAD_CELL}>Status</TableCell>
															<TableCell sx={TABLE_HEAD_CELL}>Message ID</TableCell>
															<TableCell sx={TABLE_HEAD_CELL}>Error</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{recipients.map((r, i) => (
															<TableRow key={i}>
																<TableCell sx={TABLE_BODY_CELL}>{r.email}</TableCell>
																<TableCell sx={TABLE_BODY_CELL}>{r.user_id || "—"}</TableCell>
																<TableCell sx={TABLE_BODY_CELL}><StatusChip label={r.status} /></TableCell>
																<TableCell sx={TABLE_BODY_CELL}>{r.message_id || "—"}</TableCell>
																<TableCell sx={TABLE_BODY_CELL}>{r.error_message || "—"}</TableCell>
															</TableRow>
														))}
													</TableBody>
												</Table>
											</TableCell>
										</TableRow>
									)}
								</React.Fragment>
							))}
						</TableBody>
					</Table>
				</Box>
			)}
		</Box>
	);
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function EmailMarketingPage() {
	const [creators, setCreators] = useState([]);
	const [creatorId, setCreatorId] = useState("");
	const [campaigns, setCampaigns] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [tab, setTab] = useState(0);

	const [previewCampaign, setPreviewCampaign] = useState(null);
	const [scheduleCampaign, setScheduleCampaign] = useState(null);
	const [runDialog, setRunDialog] = useState({ open: false, campaign: null, mode: "dry-run" });

	// Same source Dashboard.jsx uses for its creator dropdowns (/admin/creators/list).
	const loadCreators = useCallback(async () => {
		try {
			const res = await api.get("/admin/creators/list");
			const list = res?.data || [];
			setCreators(list);
			if (list.length > 0) {
				setCreatorId((prev) => prev || list[0].user_id);
			}
		} catch (err) {
			setError(err?.response?.data?.detail || err.message);
		}
	}, []);

	const loadCampaigns = useCallback(async () => {
		if (!creatorId) return;
		setLoading(true);
		setError("");
		try {
			const res = await api.get(`/admin/email/campaigns?creator_id=${encodeURIComponent(creatorId)}`);
			setCampaigns(res?.data || []);
		} catch (err) {
			setError(err?.response?.data?.detail || err.message);
		} finally {
			setLoading(false);
		}
	}, [creatorId]);

	useEffect(() => {
		loadCreators();
	}, [loadCreators]);

	useEffect(() => {
		loadCampaigns();
	}, [loadCampaigns]);

	const handleToggleActive = async (campaign) => {
		setCampaigns((prev) => prev.map((c) => (c.id === campaign.id ? { ...c, is_active: !c.is_active } : c)));
		try {
			await api.patch(`/admin/email/campaigns/${campaign.id}`, {
				creator_id: creatorId,
				is_active: !campaign.is_active,
			});
		} catch (err) {
			setError(err?.response?.data?.detail || err.message);
			setCampaigns((prev) => prev.map((c) => (c.id === campaign.id ? { ...c, is_active: campaign.is_active } : c)));
		}
	};

	const selectSx = {
		color: "#ddd",
		backgroundColor: "#1e1e1e",
		fontSize: 12,
		"& .MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
	};

	return (
		<>
			<Helmet>
				<title>Email marketing</title>
			</Helmet>
			<Box maxWidth={1600} mx="auto">
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
					<Box>
						<Typography variant="h3" color="#fff" fontSize={22}>Email marketing</Typography>
						<Typography sx={{ color: "#555", fontSize: 12, mt: 0.5 }}>
							Re-engagement campaigns sent via SES from reya@tlknow.ai
						</Typography>
					</Box>

					<Stack direction="row" spacing={1.5} alignItems="center">
						<Select
							size="small"
							value={creatorId}
							onChange={(e) => setCreatorId(e.target.value)}
							displayEmpty
							sx={{ ...selectSx, minWidth: 200 }}
						>
							<MenuItem value="" disabled>Select creator</MenuItem>
							{creators.map((c) => (
								<MenuItem key={c.user_id} value={c.user_id}>
									{c.username || `${c.first_name || ""} ${c.last_name || ""}`.trim()}
								</MenuItem>
							))}
						</Select>
						<Tooltip title="Reload campaigns">
							<IconButton onClick={loadCampaigns} size="small" sx={{ color: "#888", border: "1px solid #333", borderRadius: 1 }}>
								<RefreshIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Stack>
				</Stack>

				{error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

				<Tabs
					value={tab}
					onChange={(_, v) => setTab(v)}
					sx={{
						mb: 2,
						minHeight: 36,
						"& .MuiTab-root": { color: "#888", minHeight: 36, fontSize: 12, textTransform: "none" },
						"& .Mui-selected": { color: "#fff !important" },
						"& .MuiTabs-indicator": { backgroundColor: WARN },
					}}
				>
					<Tab label="Campaigns" />
					<Tab label="History" icon={<HistoryIcon fontSize="small" />} iconPosition="start" />
				</Tabs>

				{tab === 0 &&
					(loading ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
							<CircularProgress />
						</Box>
					) : (
						<Grid container spacing={2}>
							{campaigns.length === 0 && !error && (
								<Grid item xs={12}>
									<Box borderRadius={2} p={2.5} sx={CHART_STYLE}>
										<Typography sx={{ color: "#555", fontSize: 13, textAlign: "center", py: 2 }}>
											{creatorId ? "No campaigns found for this creator." : "Select a creator to see campaigns."}
										</Typography>
									</Box>
								</Grid>
							)}
							{campaigns.map((campaign) => (
								<Grid item xs={12} md={6} key={campaign.id}>
									<Box
										borderRadius={2}
										p={2.5}
										sx={{
											...CHART_STYLE,
											border: "1px solid",
											borderColor: campaign.is_active ? ACCENT : "#2a2a2a",
										}}
									>
										<Stack direction="row" justifyContent="space-between" alignItems="flex-start">
											<Box>
												<Typography sx={{ ...CARD_TITLE, mb: 0 }}>{campaign.name || campaign.campaign_id}</Typography>
												<Typography sx={{ color: "#888", fontSize: 12, mt: 0.5 }}>{campaign.subject}</Typography>
											</Box>
											<Switch
												checked={!!campaign.is_active}
												onChange={() => handleToggleActive(campaign)}
												sx={{
													"& .Mui-checked": { color: ACCENT },
													"& .Mui-checked + .MuiSwitch-track": { backgroundColor: ACCENT },
												}}
											/>
										</Stack>

										<Stack direction="row" spacing={1} sx={{ mt: 1.5, mb: 2 }} flexWrap="wrap" useFlexGap>
											<StatusChip label={campaign.is_active ? "active" : "inactive"} />
											{campaign.cron_expression && (
												<Chip size="small" variant="outlined" label={campaign.cron_expression} sx={{ color: "#888", borderColor: "#333", fontSize: 11 }} />
											)}
											<Chip size="small" variant="outlined" label={`Cooldown ${campaign.cooldown_days ?? 7}d`} sx={{ color: "#888", borderColor: "#333", fontSize: 11 }} />
											<Chip size="small" variant="outlined" label={`Last run: ${timeAgo(campaign.last_run_at)}`} sx={{ color: "#888", borderColor: "#333", fontSize: 11 }} />
										</Stack>

										<Divider sx={{ mb: 2, borderColor: "#2a2a2a" }} />

										<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
											<Button size="small" startIcon={<VisibilityIcon fontSize="small" />} onClick={() => setPreviewCampaign(campaign)} sx={{ color: "#ccc" }}>
												Preview
											</Button>
											<Button size="small" startIcon={<EditCalendarIcon fontSize="small" />} onClick={() => setScheduleCampaign(campaign)} sx={{ color: "#ccc" }}>
												Schedule
											</Button>
											<Button size="small" startIcon={<PlayArrowIcon fontSize="small" />} onClick={() => setRunDialog({ open: true, campaign, mode: "dry-run" })} sx={{ color: "#ccc" }}>
												Dry run
											</Button>
											<Button size="small" color="error" startIcon={<SendIcon fontSize="small" />} onClick={() => setRunDialog({ open: true, campaign, mode: "send" })}>
												Send now
											</Button>
										</Stack>
									</Box>
								</Grid>
							))}
						</Grid>
					))}

				{tab === 1 && <HistoryTab creatorId={creatorId} />}

				<PreviewDialog open={!!previewCampaign} onClose={() => setPreviewCampaign(null)} campaign={previewCampaign} creatorId={creatorId} />

				<ScheduleDialog
					open={!!scheduleCampaign}
					onClose={() => setScheduleCampaign(null)}
					campaign={scheduleCampaign}
					creatorId={creatorId}
					onSaved={loadCampaigns}
				/>

				<RunDialog
					open={runDialog.open}
					onClose={() => setRunDialog({ open: false, campaign: null, mode: "dry-run" })}
					campaign={runDialog.campaign}
					creatorId={creatorId}
					mode={runDialog.mode}
					onCompleted={loadCampaigns}
				/>
			</Box>
		</>
	);
}